import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  ArcElement,
  BarElement,
  Chart as ChartJS,
  CategoryScale,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import { Bar, Line, Pie } from "react-chartjs-2";
import { Select } from "antd";
import { CareShell, Panel } from "../../components/care/CareLayout";
import { useAuth } from "../../contexts/AuthContext";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import { careService } from "../../services/careService";
import AuthLoading from "../../components/common/AuthLoading";

ChartJS.register(ArcElement, BarElement, CategoryScale, Filler, Legend, LinearScale, LineElement, PointElement, Tooltip);

const chartPalette = ["#1e82e5", "#14a444", "#c2185b", "#7c3aed", "#f59e0b", "#0891b2", "#be123c", "#2563eb"];
const payCodePalette = {
  REGULAR: "#1e82e5",
  OVERTIME: "#f59e0b",
};
const clean = (value) => String(value || "").trim();
const currentUserId = (user = {}) => user?.id || user?.userId || user?.uid || "";
const normalizeRoleToken = (role) =>
  String(role || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (days) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().slice(0, 10);
};
const toTimestampMillis = (value) => {
  if (!value) return null;
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : null;
};
const hoursBetween = (start, end) => {
  const startMs = toTimestampMillis(start);
  const endMs = toTimestampMillis(end);
  if (startMs === null || endMs === null) return 0;
  return Math.max(0, endMs - startMs) / 3600000;
};
const formatHours = (value) => `${Number(value || 0).toFixed(2)} hrs`;
const titleCase = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
const rowEntries = (row = {}) => row.entries || row.timeEntries || [];
const entryStart = (entry = {}) => entry.startTimestamp || entry.startAt || entry.startDateTime || entry.startTime || "";
const entryEnd = (entry = {}) => entry.endTimestamp || entry.endAt || entry.endDateTime || entry.endTime || "";
const statusFor = (row = {}) => String(row.status || "SUBMITTED").toUpperCase();
const userFor = (profile = {}) => profile?.userProfile || profile?.user || {};
const profileFor = (profile = {}) => profile?.profile || profile?.profileData || {};
const nameFor = (profile = {}) => {
  const user = userFor(profile);
  const base = profileFor(profile);
  return clean(user.name) ||
    clean(base.fullName) ||
    [base.firstName, base.middleName, base.lastName].map(clean).filter(Boolean).join(" ") ||
    clean(profile.name) ||
    "Care staff";
};
const profileIdFor = (profile = {}) => profile.id || profile.careProfileId || "";
const normalizeCareProfile = (result) => {
  if (Array.isArray(result)) return result[0] || null;
  if (Array.isArray(result?.content)) return result.content[0] || null;
  if (Array.isArray(result?.data)) return result.data[0] || null;
  return result || null;
};
const userIdFor = (profile = {}) => {
  const user = userFor(profile);
  return user.id || profile.userProfileId || profile.userId || "";
};
const staffNameFor = (row = {}, profilesById = {}) => {
  const profile = profilesById[row.careProfileId] || profilesById[row.profileId] || profilesById[row.caregiverUserId];
  return clean(row.caregiverName) ||
    clean(row.staffName) ||
    clean(row.userProfile?.name) ||
    clean(row.caregiver?.name) ||
    (profile ? nameFor(profile) : "") ||
    clean(row.caregiverEmail) ||
    clean(row.caregiverUserId) ||
    "Care staff";
};
const staffEmailFor = (row = {}, profilesById = {}) => {
  const profile = profilesById[row.careProfileId] || profilesById[row.profileId] || profilesById[row.caregiverUserId];
  return clean(row.caregiverEmail) ||
    clean(row.userProfile?.email) ||
    clean(row.caregiver?.email) ||
    profile?.userProfile?.email ||
    profile?.user?.email ||
    profile?.profile?.email ||
    profile?.profileData?.email ||
    "";
};
const dateLabelsBetween = (start, end) => {
  const labels = [];
  const current = new Date(`${start}T00:00:00`);
  const last = new Date(`${end}T00:00:00`);
  if (Number.isNaN(current.getTime()) || Number.isNaN(last.getTime())) return labels;
  while (current <= last) {
    labels.push(current.toISOString().slice(0, 10));
    current.setDate(current.getDate() + 1);
  }
  return labels;
};
const friendlyDate = (value) => {
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString([], { month: "short", day: "numeric" });
};
const ADMIN_REVIEW_STATUSES = ["APPROVED", "DECLINED", "PAID"];
const STATUS_OPTIONS = ["SUBMITTED", "APPROVED", "PAID", "DECLINED", "CANCELLED"];

export default function CareStaffTimesheetsChartPage({ scope = "workspace" }) {
  const { user, careRoles = [], refreshCareRoles } = useAuth();
  const { selectedWorkspaceId, workspaceListReady } = useWorkspace();
  const actorId = currentUserId(user);
  const isMyTimesheet = scope === "mine";
  const [resolvedCareRoles, setResolvedCareRoles] = useState(() => careRoles);
  const [rolesRefreshing, setRolesRefreshing] = useState(Boolean(refreshCareRoles));
  const normalizedCareRoles = resolvedCareRoles.map(normalizeRoleToken);
  const canViewWorkspaceTimesheets = normalizedCareRoles.includes("care_admin") || normalizedCareRoles.includes("care_owner");
  const visibleStatusOptions = canViewWorkspaceTimesheets
    ? STATUS_OPTIONS
    : STATUS_OPTIONS.filter((status) => !ADMIN_REVIEW_STATUSES.includes(status));
  const [timesheets, setTimesheets] = useState([]);
  const [careProfiles, setCareProfiles] = useState([]);
  const [rangeStart, setRangeStart] = useState(() => daysAgo(29));
  const [rangeEnd, setRangeEnd] = useState(() => today());
  const [selectedStaffIds, setSelectedStaffIds] = useState([]);
  const [statusFilter, setStatusFilter] = useState("ACTIVE");
  const [payCodeFilter, setPayCodeFilter] = useState("BOTH");
  const [chartMode, setChartMode] = useState("EMPLOYEE");
  const [chartType, setChartType] = useState("LINE");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!refreshCareRoles) setResolvedCareRoles(careRoles);
  }, [careRoles, refreshCareRoles]);

  useEffect(() => {
    let active = true;
    if (!refreshCareRoles) {
      setRolesRefreshing(false);
      return () => {
        active = false;
      };
    }
    if (!workspaceListReady || !actorId) {
      setRolesRefreshing(true);
      return () => {
        active = false;
      };
    }
    if (!selectedWorkspaceId) {
      setRolesRefreshing(false);
      return () => {
        active = false;
      };
    }

    setRolesRefreshing(true);
    refreshCareRoles({ workspaceId: selectedWorkspaceId, wid: selectedWorkspaceId })
      .then((nextRoles) => {
        if (active) setResolvedCareRoles(Array.isArray(nextRoles) ? nextRoles : []);
      })
      .finally(() => {
        if (active) setRolesRefreshing(false);
      });

    return () => {
      active = false;
    };
  }, [actorId, refreshCareRoles, selectedWorkspaceId, workspaceListReady]);

  useEffect(() => {
    let active = true;
    if (!workspaceListReady || !selectedWorkspaceId) {
      setLoading(false);
      return () => {
        active = false;
      };
    }
    setLoading(true);
    setError("");
    const workspaceParams = { actorId, wid: selectedWorkspaceId, workspaceId: selectedWorkspaceId };
    const requests = isMyTimesheet
      ? [
          careService.careProfiles.getMine({
            actorId,
            userId: actorId,
            workspaceId: selectedWorkspaceId,
            wid: selectedWorkspaceId,
          }),
        ]
      : [
          careService.timesheets.list(workspaceParams),
          careService.careProfiles.list(workspaceParams),
        ];
    Promise.allSettled(requests)
      .then(([timesheetsResult, profilesResult]) => {
        if (!active) return;
        if (isMyTimesheet) {
          const profile = timesheetsResult.status === "fulfilled" ? normalizeCareProfile(timesheetsResult.value) : null;
          const myCareProfileId = profileIdFor(profile || {});
          setCareProfiles(profile ? [profile] : []);
          if (!myCareProfileId) {
            setTimesheets([]);
            setError(timesheetsResult.reason?.message || "No completed care profile found for your account.");
            return;
          }
          return careService.timesheets.listForCareProfile(myCareProfileId, workspaceParams)
            .then((rows) => {
              if (!active) return;
              const serverRows = Array.isArray(rows) ? rows : [];
              setTimesheets(serverRows);
            })
            .catch((loadError) => {
              if (!active) return;
              setTimesheets([]);
              setError(loadError?.response?.data?.message || loadError?.message || "Unable to load timesheets.");
            })
            .finally(() => active && setLoading(false));
          return;
        }
        if (profilesResult.status === "fulfilled") {
          setCareProfiles(Array.isArray(profilesResult.value) ? profilesResult.value : []);
        }
        if (timesheetsResult.status === "fulfilled") {
          const rows = Array.isArray(timesheetsResult.value) ? timesheetsResult.value : [];
          setTimesheets(rows);
        } else {
          setTimesheets([]);
          setError(timesheetsResult.reason?.response?.data?.message || timesheetsResult.reason?.message || "Unable to load timesheets.");
        }
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [actorId, isMyTimesheet, selectedWorkspaceId, workspaceListReady]);

  useEffect(() => {
    if (!canViewWorkspaceTimesheets && ADMIN_REVIEW_STATUSES.includes(statusFilter)) {
      setStatusFilter("ACTIVE");
    }
  }, [canViewWorkspaceTimesheets, statusFilter]);

  const profilesById = useMemo(() => {
    const lookup = {};
    careProfiles.forEach((profile) => {
      const profileId = profileIdFor(profile);
      const userId = userIdFor(profile);
      if (profileId) lookup[profileId] = profile;
      if (userId) lookup[userId] = profile;
    });
    return lookup;
  }, [careProfiles]);
  const staffChoices = useMemo(
    () => careProfiles
      .map((profile) => ({
        id: profileIdFor(profile),
        userId: userIdFor(profile),
        name: nameFor(profile),
        email: profile?.userProfile?.email || profile?.user?.email || profile?.profile?.email || profile?.profileData?.email || "",
      }))
      .filter((item) => item.id)
      .sort((left, right) => left.name.localeCompare(right.name)),
    [careProfiles],
  );
  const selectedStaffLookup = useMemo(() => {
    if (!selectedStaffIds.length) return null;
    const lookup = new Set(selectedStaffIds);
    staffChoices.forEach((staff) => {
      if (lookup.has(staff.id) && staff.userId) lookup.add(staff.userId);
    });
    return lookup;
  }, [selectedStaffIds, staffChoices]);
  const chartEntries = useMemo(() => {
    return timesheets
      .filter((row) => {
        if (statusFilter === "ACTIVE") return !["CANCELLED", "DECLINED"].includes(statusFor(row));
        if (statusFilter !== "ALL") return statusFor(row) === statusFilter;
        return true;
      })
      .flatMap((row) => {
        const entries = rowEntries(row);
        return entries.map((entry) => {
          const start = entryStart(entry);
          const end = entryEnd(entry);
          const payCode = String(entry.payCode || entry.shiftType || row.payCode || row.shiftType || "REGULAR").toUpperCase();
          const date = String(start || row.workDate || row.date || "").slice(0, 10);
          const careProfileId = row.careProfileId || row.profileId || row.caregiverUserId || staffNameFor(row, profilesById);
          const userId = row.caregiverUserId || "";
          return {
            careProfileId,
            userId,
            staffKey: careProfileId,
            staffName: staffNameFor(row, profilesById),
            email: staffEmailFor(row, profilesById),
            date,
            payCode,
            hours: Number(entry.hours ?? hoursBetween(start, end)),
          };
        });
      })
      .filter((entry) => {
        if (!entry.date || (rangeStart && entry.date < rangeStart) || (rangeEnd && entry.date > rangeEnd)) return false;
        if (selectedStaffLookup && !selectedStaffLookup.has(entry.careProfileId) && !selectedStaffLookup.has(entry.userId)) return false;
        if (payCodeFilter !== "BOTH" && entry.payCode !== payCodeFilter) return false;
        return entry.hours > 0;
      });
  }, [payCodeFilter, profilesById, rangeEnd, rangeStart, selectedStaffLookup, statusFilter, timesheets]);

  const labels = useMemo(() => dateLabelsBetween(rangeStart, rangeEnd), [rangeEnd, rangeStart]);
  const staffTotals = useMemo(() => {
    const lookup = new Map();
    chartEntries.forEach((entry) => {
      const current = lookup.get(entry.staffKey) || {
        staffKey: entry.staffKey,
        name: entry.staffName,
        email: entry.email,
        total: 0,
        byDate: {},
      };
      current.total += entry.hours;
      current.byDate[entry.date] = (current.byDate[entry.date] || 0) + entry.hours;
      lookup.set(entry.staffKey, current);
    });
    return [...lookup.values()].sort((left, right) => right.total - left.total);
  }, [chartEntries]);
  const payCodeTotals = useMemo(() => {
    const lookup = new Map();
    chartEntries.forEach((entry) => {
      const current = lookup.get(entry.payCode) || {
        payCode: entry.payCode,
        label: titleCase(entry.payCode),
        total: 0,
        byDate: {},
      };
      current.total += entry.hours;
      current.byDate[entry.date] = (current.byDate[entry.date] || 0) + entry.hours;
      lookup.set(entry.payCode, current);
    });
    const order = ["REGULAR", "OVERTIME"];
    return [...lookup.values()].sort((left, right) => order.indexOf(left.payCode) - order.indexOf(right.payCode));
  }, [chartEntries]);
  const chartSeries = useMemo(
    () => (chartMode === "PAY_CODE" ? payCodeTotals : staffTotals),
    [chartMode, payCodeTotals, staffTotals],
  );
  const chartData = useMemo(() => {
    if (chartType === "PIE") {
      return {
        labels: chartSeries.map((series) => (chartMode === "PAY_CODE" ? series.label : series.name)),
        datasets: [{
          data: chartSeries.map((series) => Number((series.total || 0).toFixed(2))),
          backgroundColor: chartSeries.map((series, index) => (
            chartMode === "PAY_CODE"
              ? payCodePalette[series.payCode] || chartPalette[index % chartPalette.length]
              : chartPalette[index % chartPalette.length]
          )),
          borderColor: "#ffffff",
          borderWidth: 2,
        }],
      };
    }
    return {
      labels: labels.map(friendlyDate),
      datasets: chartSeries.map((series, index) => {
      const color = chartMode === "PAY_CODE"
        ? payCodePalette[series.payCode] || chartPalette[index % chartPalette.length]
        : chartPalette[index % chartPalette.length];
      const label = chartMode === "PAY_CODE" ? series.label : series.name;
      return {
        label,
        data: labels.map((dateLabel) => Number((series.byDate[dateLabel] || 0).toFixed(2))),
        borderColor: color,
        backgroundColor: chartType === "BAR" ? `${color}b8` : `${color}24`,
        pointBackgroundColor: color,
        pointRadius: 3,
        pointHoverRadius: 5,
        borderWidth: 3,
        fill: false,
        tension: 0.32,
      };
      }),
    };
  }, [chartMode, chartSeries, chartType, labels]);
  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: { boxWidth: 12, color: "#243047", font: { weight: 700 } },
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const label = context.dataset.label || context.label || "Hours";
            const value = typeof context.parsed === "number" ? context.parsed : context.parsed?.y;
            return `${label}: ${formatHours(value)}`;
          },
        },
      },
    },
    scales: chartType === "PIE" ? undefined : {
      x: { grid: { display: false }, ticks: { color: "#64748b" } },
      y: {
        beginAtZero: true,
        grid: { color: "rgba(148, 163, 184, 0.22)" },
        ticks: { color: "#64748b", callback: (value) => `${value}h` },
      },
    },
  }), [chartType]);
  const totalHours = useMemo(() => staffTotals.reduce((sum, staff) => sum + staff.total, 0), [staffTotals]);
  const activeSeriesCount = chartSeries.length;
  const ChartComponent = chartType === "BAR" ? Bar : chartType === "PIE" ? Pie : Line;

  if (!isMyTimesheet && rolesRefreshing) {
    return <AuthLoading />;
  }

  if (!isMyTimesheet && !canViewWorkspaceTimesheets) {
    return <Navigate to="/my-timesheet/chart" replace />;
  }

  return (
    <CareShell className="dashboard-content residents-page care-staff-timesheets-page timesheet-chart-page">
      <div className="residents-page-head">
        <div className="page-title">
          <h1>{isMyTimesheet ? "My Timesheet Hours Chart" : "Timesheet Hours Chart"}</h1>
          <p>{isMyTimesheet ? "Review your regular and overtime hours across the selected date range." : "Compare hours worked by care staff across the selected date range."}</p>
        </div>
        <div className="care-staff-page-actions">
          <Link className="mini-btn timesheet-nav-blue" to={isMyTimesheet ? "/my-timesheet" : "/timesheets"}>
            <i className="ti ti-arrow-left" /> Back to {isMyTimesheet ? "my timesheet" : "timesheets"}
          </Link>
        </div>
      </div>

      {loading ? <Panel title="Hours worked"><p>Loading chart data...</p></Panel> : null}
      {error ? <Panel title="Hours worked"><p className="form-error">{error}</p></Panel> : null}

      <section className="timesheet-chart-controls">
        <article>
          <span>Total hours</span>
          <strong>{formatHours(totalHours)}</strong>
          <small>{staffTotals.length} employee{staffTotals.length === 1 ? "" : "s"}</small>
        </article>
        <label>
          Range start
          <input type="date" value={rangeStart} onChange={(event) => setRangeStart(event.target.value)} />
        </label>
        <label>
          Range end
          <input type="date" value={rangeEnd} onChange={(event) => setRangeEnd(event.target.value)} />
        </label>
        <label>
          Status
          <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
            <option value="ACTIVE">Active records</option>
            <option value="ALL">All statuses</option>
            {visibleStatusOptions.map((status) => (
              <option value={status} key={status}>{titleCase(status)}</option>
            ))}
          </select>
        </label>
        <label>
          Plot by
          <select value={chartMode} onChange={(event) => setChartMode(event.target.value)}>
            <option value="EMPLOYEE">Employees</option>
            <option value="PAY_CODE">Regular vs overtime</option>
          </select>
        </label>
        <label>
          Chart type
          <select value={chartType} onChange={(event) => setChartType(event.target.value)}>
            <option value="LINE">Line graph</option>
            <option value="BAR">Bar graph</option>
            <option value="PIE">Pie chart</option>
          </select>
        </label>
        <label>
          Pay code
          <select value={payCodeFilter} onChange={(event) => setPayCodeFilter(event.target.value)}>
            <option value="BOTH">Regular and overtime</option>
            <option value="REGULAR">Regular only</option>
            <option value="OVERTIME">Overtime only</option>
          </select>
        </label>
        {!isMyTimesheet ? (
        <label className="timesheet-chart-staff-filter">
          Employees
          <Select
            mode="multiple"
            allowClear
            showSearch
            value={selectedStaffIds}
            placeholder="All employees"
            optionFilterProp="searchText"
            onChange={(value) => setSelectedStaffIds(value)}
            options={staffChoices.map((staff) => ({
              value: staff.id,
              searchText: `${staff.name} ${staff.email}`,
              label: (
                <span className="timesheet-staff-option">
                  <strong>{staff.name}</strong>
                  <small>{staff.email || "No email"}</small>
                </span>
              ),
            }))}
          />
        </label>
        ) : null}
      </section>

      <Panel title={chartMode === "PAY_CODE" ? "Regular vs Overtime Hours" : "Hours Worked Over Time"}>
        <div className={`timesheet-chart-shell${chartType === "PIE" ? " is-pie" : ""}`}>
          {activeSeriesCount ? <ChartComponent data={chartData} options={chartOptions} /> : (
            <div className="timesheet-chart-empty">
              <i className="ti ti-chart-line" />
              <span>No hours found for this range.</span>
            </div>
          )}
        </div>
      </Panel>

      <Panel title={isMyTimesheet ? "My totals" : "Employee totals"}>
        <div className="timesheet-chart-table">
          <div className="timesheet-chart-table-row is-head">
            <span>{isMyTimesheet ? "Name" : "Employee"}</span>
            <span>Email</span>
            <span>Total hours</span>
          </div>
          {staffTotals.length ? staffTotals.map((staff) => (
            <div className="timesheet-chart-table-row" key={staff.staffKey}>
              <strong>{staff.name}</strong>
              <span>{staff.email || "No email"}</span>
              <b>{formatHours(staff.total)}</b>
            </div>
          )) : (
            <div className="timesheet-empty">
              <i className="ti ti-calendar-time" />
              <span>No employee totals to show.</span>
            </div>
          )}
        </div>
      </Panel>
    </CareShell>
  );
}
